/**
 * Wizard card for the stape_domain + stape_loader + stape_settings steps (Slice 8).
 *
 * One card (rendered for stape_domain) drives all three rows via a single
 * `provision` action: it adds the client's `br.{domain}` to the shared Stape
 * container (yielding both br + load.br CNAME records), reads the container-level
 * Custom Loader, and writes the stape_* settings (wiring the viewer to Stape).
 *
 * No secret is handled here — the Stape API key + container are platform env
 * vars. The card surfaces the CNAME records the client must add to DNS (which
 * the Slice 2 DNS email also consumes). The card derives its state from the
 * stape_domain task result (mirrored with the loader/settings outcome).
 */
import { CheckCircle2, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'

interface StapeCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface StapeDnsRecord {
  type: string
  host: string
  value: string
  domain: string
}

interface StapeResult {
  stapeDomainName?: string
  records?: StapeDnsRecord[]
  dnsStatus?: string
  reused?: boolean
  loaderDomain?: string
  scriptPath?: string
  dataLayerToken?: string
}

export function StapeCard({ task, accountId, runId }: StapeCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as StapeResult
  const params = (task.params ?? {}) as { mainDomain?: string }
  const isDone = task.status === 'done'
  const records = result.records ?? []
  const dnsReady = result.dnsStatus === 'ok'

  const provision = () => {
    invokeStep.mutate(
      {
        functionName: 'onboard-stape',
        accountId,
        params: { action: 'provision' },
      },
      {
        onError: (error) =>
          toast({
            title: 'Stape provisioning failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            Provisioning adds <code>br.{params.mainDomain ?? '{domain}'}</code> to
            the shared Stape container and reads the container-level Custom
            Loader. The two CNAME records below must be added to the
            client&rsquo;s DNS (they&rsquo;re also included in the DNS email).
          </p>
          <p>
            Platform prerequisites: <code>STAPE_API_KEY</code> +{' '}
            <code>STAPE_CONTAINER_ID</code> edge-function secrets, and a Custom
            Loader configured once on the shared container.
          </p>
        </div>
      }
    >
      {isDone ? (
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              Stape provisioned for{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {result.stapeDomainName}
              </code>
              {result.reused ? ' (reused)' : ''}
            </span>
          </div>

          {records.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                DNS records for the client {dnsReady ? '(validated ✓)' : '(pending — set these)'}:
              </p>
              <div className="overflow-hidden rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium">Type</th>
                      <th className="px-2 py-1 text-left font-medium">Host</th>
                      <th className="px-2 py-1 text-left font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y font-mono">
                    {records.map((r) => (
                      <tr key={`${r.host}.${r.domain}`}>
                        <td className="px-2 py-1 uppercase">{r.type}</td>
                        <td className="px-2 py-1">
                          {r.host}.{r.domain}
                        </td>
                        <td className="px-2 py-1">{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.scriptPath && (
            <div className="space-y-1 rounded-md border bg-muted/40 p-2">
              <p className="text-xs font-medium text-muted-foreground">
                Loader values (written to the account&rsquo;s settings):
              </p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                <dt className="text-muted-foreground">Stape domain</dt>
                <dd className="break-all font-mono">{result.loaderDomain}</dd>
                <dt className="text-muted-foreground">Script path</dt>
                <dd className="break-all font-mono">{result.scriptPath}</dd>
                <dt className="text-muted-foreground">Data layer token</dt>
                <dd className="break-all font-mono">{result.dataLayerToken}</dd>
              </dl>
            </div>
          )}

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={provision}
            disabled={invokeStep.isPending}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-run / reconcile
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Adds <code>br.{params.mainDomain ?? '{domain}'}</code> to the shared
            Stape container and wires the viewer to server-side GTM. Returns the
            CNAME records to give the client.
          </p>
          <Button type="button" size="sm" onClick={provision} disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Provision Stape
          </Button>
        </div>
      )}
    </StepCard>
  )
}
